import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { m } from 'framer-motion';
import { FiAlertCircle, FiCheckCircle, FiLoader, FiSearch, FiChevronDown, FiArrowUp, FiArrowDown, FiMinus } from 'react-icons/fi';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import { SITE_ORIGIN } from './siteConfig';
import { DEFAULT_PAGE_TITLE } from './seoDefaults';

const PATH = '/competitor-tracker';
const CANONICAL = `${SITE_ORIGIN}${PATH}`;

function ScoreRing({ value, label, size = 80 }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const offset = value != null ? circ - (circ * value) / 100 : circ;
  const color = value == null ? '#94a3b8' : value >= 80 ? '#10b981' : value >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth="6" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="6"
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
          className="fill-slate-900 font-bold" style={{ fontSize: size * 0.28 }}>
          {value != null ? `${value}%` : '—'}
        </text>
      </svg>
      {label && <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide text-center">{label}</span>}
    </div>
  );
}

function DiffIndicator({ a, b, inverse }) {
  if (a == null || b == null) return <FiMinus className="text-slate-400" size={14} />;
  const diff = a - b;
  if (diff === 0) return <FiMinus className="text-slate-400" size={14} />;
  const better = inverse ? diff < 0 : diff > 0;
  return better
    ? <FiArrowUp className="text-emerald-600" size={14} />
    : <FiArrowDown className="text-red-500" size={14} />;
}

function CompareRow({ label, valA, valB, unitA, unitB, inverse }) {
  const uA = unitA || '';
  const uB = unitB || '';
  const dispA = valA != null ? `${valA}${uA}` : '—';
  const dispB = valB != null ? `${valB}${uB}` : '—';
  return (
    <tr className="border-b border-slate-100 last:border-0 text-sm">
      <td className="py-2.5 pr-3 text-slate-700 font-medium">{label}</td>
      <td className="py-2.5 px-2 text-center font-semibold tabular-nums text-slate-900">
        <span className="inline-flex items-center gap-1">{dispA} <DiffIndicator a={valA} b={valB} inverse={inverse} /></span>
      </td>
      <td className="py-2.5 pl-2 text-center font-semibold tabular-nums text-slate-900">{dispB}</td>
    </tr>
  );
}

function CheckCompare({ title, checksA, checksB, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  if ((!checksA || !checksA.length) && (!checksB || !checksB.length)) return null;
  const labels = [...new Set([...(checksA || []).map(c => c.label), ...(checksB || []).map(c => c.label)])];
  const mapA = Object.fromEntries((checksA || []).map(c => [c.label, c.pass]));
  const mapB = Object.fromEntries((checksB || []).map(c => [c.label, c.pass]));
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-left font-semibold text-slate-900 text-sm transition-colors"
        aria-expanded={open}>
        {title}
        <FiChevronDown className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-500 uppercase tracking-wide">
                <th className="text-left py-2 px-4 font-medium">Check</th>
                <th className="text-center py-2 px-2 font-medium">Your site</th>
                <th className="text-center py-2 px-2 font-medium">Competitor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {labels.map(l => (
                <tr key={l}>
                  <td className="py-2 px-4 text-slate-700">{l}</td>
                  <td className="py-2 px-2 text-center">
                    {mapA[l] === true ? <FiCheckCircle className="inline text-emerald-600" /> : mapA[l] === false ? <FiAlertCircle className="inline text-amber-600" /> : <FiMinus className="inline text-slate-300" />}
                  </td>
                  <td className="py-2 px-2 text-center">
                    {mapB[l] === true ? <FiCheckCircle className="inline text-emerald-600" /> : mapB[l] === false ? <FiAlertCircle className="inline text-amber-600" /> : <FiMinus className="inline text-slate-300" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function CompetitorTrackerPage() {
  const urlAId = useId();
  const urlBId = useId();
  const helperId = `${urlAId}-helper`;
  const statusId = `${urlAId}-status`;
  const resultsRef = useRef(null);

  const [urlA, setUrlA] = useState('');
  const [urlB, setUrlB] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = 'Free SEO competitor comparison — side-by-side audit | WebCodeCraft';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', CANONICAL);
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute('content') : '';
    if (desc) desc.setAttribute('content', 'Compare two websites side-by-side: SEO scores, Core Web Vitals, schema, security headers and more. Free, no login. WebCodeCraft.');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const twTitle = document.querySelector('meta[property="twitter:title"]');
    const twDesc = document.querySelector('meta[property="twitter:description"]');
    const twUrl = document.querySelector('meta[property="twitter:url"]');
    const pageTitle = 'Free SEO competitor comparison — side-by-side audit | WebCodeCraft';
    const pageDesc = 'Compare two websites side-by-side: SEO scores, Core Web Vitals, schema, security headers and more. Free, no login. WebCodeCraft.';
    if (ogTitle) ogTitle.setAttribute('content', pageTitle);
    if (ogDesc) ogDesc.setAttribute('content', pageDesc);
    if (ogUrl) ogUrl.setAttribute('content', CANONICAL);
    if (twTitle) twTitle.setAttribute('content', pageTitle);
    if (twDesc) twDesc.setAttribute('content', pageDesc);
    if (twUrl) twUrl.setAttribute('content', CANONICAL);
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org', '@type': 'WebPage',
      name: pageTitle, url: CANONICAL, description: pageDesc,
      isPartOf: { '@type': 'WebSite', name: 'WebCodeCraft', url: SITE_ORIGIN },
      publisher: { '@type': 'Organization', name: 'WebCodeCraft', url: SITE_ORIGIN }
    });
    ld.setAttribute('data-page', 'competitor-tracker');
    document.head.appendChild(ld);
    return () => {
      document.title = DEFAULT_PAGE_TITLE;
      if (canonical) canonical.setAttribute('href', `${SITE_ORIGIN}/`);
      if (desc && prevDesc != null) desc.setAttribute('content', prevDesc);
      ld.remove();
    };
  }, []);

  const runCompare = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    setData(null);
    const tA = urlA.trim();
    const tB = urlB.trim();
    if (!tA || !tB) { setError('Enter two URLs to compare.'); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/competitor-compare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urlA: tA, urlB: tB })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) { setError(typeof json.error === 'string' ? json.error : 'Comparison failed.'); return; }
      setData(json);
      requestAnimationFrame(() => { resultsRef.current?.focus(); });
    } catch { setError('Network error. Try again in a moment.'); }
    finally { setLoading(false); }
  }, [urlA, urlB]);

  const a = data?.a;
  const b = data?.b;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <Header />
      <CookieBanner />
      <main id="main-content" className="pt-20 min-h-screen min-w-0 overflow-x-clip">
        <section className="container mx-auto px-4 py-10 max-w-3xl" aria-labelledby="comp-heading">
          <m.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <p className="text-sm font-medium text-indigo-600 mb-2">
              <Link to="/check-your-seo" className="underline underline-offset-2 hover:text-indigo-800">Tools</Link>
              <span className="mx-1 text-slate-400" aria-hidden="true">/</span>
              <span className="text-slate-700">Competitor tracker</span>
            </p>
            <h1 id="comp-heading" className="text-3xl sm:text-4xl font-bold text-slate-900 font-display tracking-tight mb-4">
              SEO competitor comparison
            </h1>
            <p className="text-slate-600 leading-relaxed mb-6" id={helperId}>
              Enter <strong>your site</strong> and a <strong>competitor's</strong> URL.
              We run the same full audit on both and show results side-by-side so you can see exactly where you're ahead — and where to improve.
            </p>
            <form onSubmit={runCompare} className="space-y-4" noValidate>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor={urlAId} className="block text-sm font-semibold text-slate-800 mb-1.5">Your site URL</label>
                  <input id={urlAId} type="url" inputMode="url" autoComplete="url"
                    placeholder="https://yourdomain.com" value={urlA}
                    onChange={(ev) => setUrlA(ev.target.value)}
                    className="form-input w-full" disabled={loading} />
                </div>
                <div>
                  <label htmlFor={urlBId} className="block text-sm font-semibold text-slate-800 mb-1.5">Competitor URL</label>
                  <input id={urlBId} type="url" inputMode="url" autoComplete="url"
                    placeholder="https://competitor.com" value={urlB}
                    onChange={(ev) => setUrlB(ev.target.value)}
                    className="form-input w-full" disabled={loading} />
                </div>
              </div>
              <m.button type="submit" className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                disabled={loading} whileTap={{ scale: 0.98 }}>
                {loading
                  ? <><FiLoader className="animate-spin" aria-hidden="true" />Comparing…</>
                  : <><FiSearch aria-hidden="true" />Compare</>}
              </m.button>
            </form>
            <div id={statusId} role="status" aria-live="polite" className="mt-6 min-h-[1.25rem] text-sm">
              {error && (
                <p className="text-red-700 font-medium flex items-start gap-2">
                  <FiAlertCircle className="shrink-0 mt-0.5" aria-hidden="true" />{error}
                </p>
              )}
              {loading && (
                <p className="text-slate-500">Running two full audits in parallel — this can take up to 60 seconds…</p>
              )}
            </div>
          </m.div>
        </section>

        {data?.ok && a && b && (
          <section ref={resultsRef} tabIndex={-1}
            className="container mx-auto px-4 pb-16 max-w-4xl outline-none space-y-8"
            aria-label="Comparison results">

            <div className="grid grid-cols-2 gap-6">
              <div className="flex flex-col items-center gap-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <ScoreRing value={a.scores.overall} size={100} />
                <p className="text-sm font-semibold text-slate-900 text-center truncate max-w-full" title={a.finalUrl}>
                  {new URL(a.finalUrl).hostname}
                </p>
                <p className="text-xs text-slate-500">Your site</p>
              </div>
              <div className="flex flex-col items-center gap-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <ScoreRing value={b.scores.overall} size={100} />
                <p className="text-sm font-semibold text-slate-900 text-center truncate max-w-full" title={b.finalUrl}>
                  {new URL(b.finalUrl).hostname}
                </p>
                <p className="text-xs text-slate-500">Competitor</p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 sm:px-6 py-4">
              <h2 className="text-lg font-bold text-slate-900 mb-3">Score summary</h2>
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-slate-500 uppercase tracking-wide">
                    <th className="text-left py-2 pr-3 font-medium">Metric</th>
                    <th className="text-center py-2 px-2 font-medium">Your site</th>
                    <th className="text-center py-2 pl-2 font-medium">Competitor</th>
                  </tr>
                </thead>
                <tbody>
                  <CompareRow label="Overall (weighted)" valA={a.scores.overall} valB={b.scores.overall} unitA="%" unitB="%" />
                  <CompareRow label="Technical" valA={a.scores.technical} valB={b.scores.technical} unitA="%" unitB="%" />
                  <CompareRow label="On-page" valA={a.scores.onPage} valB={b.scores.onPage} unitA="%" unitB="%" />
                  <CompareRow label="Visibility" valA={a.scores.visibility} valB={b.scores.visibility} unitA="%" unitB="%" />
                </tbody>
              </table>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 sm:px-6 py-4">
              <h2 className="text-lg font-bold text-slate-900 mb-3">Page metrics</h2>
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-slate-500 uppercase tracking-wide">
                    <th className="text-left py-2 pr-3 font-medium">Metric</th>
                    <th className="text-center py-2 px-2 font-medium">Your site</th>
                    <th className="text-center py-2 pl-2 font-medium">Competitor</th>
                  </tr>
                </thead>
                <tbody>
                  <CompareRow label="HTTPS" valA={a.isHttps ? 1 : 0} valB={b.isHttps ? 1 : 0} unitA={a.isHttps ? ' ✓' : ' ✗'} unitB={b.isHttps ? ' ✓' : ' ✗'} />
                  <CompareRow label="Response time" valA={a.responseTimeMs} valB={b.responseTimeMs} unitA=" ms" unitB=" ms" inverse />
                  <CompareRow label="Word count" valA={a.wordCount} valB={b.wordCount} />
                  <CompareRow label="H1 count" valA={a.h1Count} valB={b.h1Count} />
                  <CompareRow label="Title length" valA={a.title?.length || 0} valB={b.title?.length || 0} />
                  <CompareRow label="Meta desc length" valA={a.description?.length || 0} valB={b.description?.length || 0} />
                  <CompareRow label="Internal links" valA={a.links?.internalCount} valB={b.links?.internalCount} />
                  <CompareRow label="External links" valA={a.links?.externalCount} valB={b.links?.externalCount} />
                  <CompareRow label="Images with alt" valA={a.imagesAltPercent} valB={b.imagesAltPercent} unitA="%" unitB="%" />
                  <CompareRow label="JSON-LD blocks" valA={a.jsonLdCount} valB={b.jsonLdCount} />
                </tbody>
              </table>
            </div>

            {(a.psi || b.psi) && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 sm:px-6 py-4">
                <h2 className="text-lg font-bold text-slate-900 mb-3">Core Web Vitals (mobile)</h2>
                <table className="w-full">
                  <thead>
                    <tr className="text-xs text-slate-500 uppercase tracking-wide">
                      <th className="text-left py-2 pr-3 font-medium">Metric</th>
                      <th className="text-center py-2 px-2 font-medium">Your site</th>
                      <th className="text-center py-2 pl-2 font-medium">Competitor</th>
                    </tr>
                  </thead>
                  <tbody>
                    <CompareRow label="Performance" valA={a.psi?.performanceScore} valB={b.psi?.performanceScore} unitA="%" unitB="%" />
                    <CompareRow label="Accessibility" valA={a.psi?.accessibilityScore} valB={b.psi?.accessibilityScore} unitA="%" unitB="%" />
                    <CompareRow label="LCP" valA={a.psi?.lcp != null ? +(a.psi.lcp / 1000).toFixed(1) : null} valB={b.psi?.lcp != null ? +(b.psi.lcp / 1000).toFixed(1) : null} unitA=" s" unitB=" s" inverse />
                    <CompareRow label="CLS" valA={a.psi?.cls} valB={b.psi?.cls} inverse />
                    <CompareRow label="INP" valA={a.psi?.inp} valB={b.psi?.inp} unitA=" ms" unitB=" ms" inverse />
                    <CompareRow label="FCP" valA={a.psi?.fcp != null ? +(a.psi.fcp / 1000).toFixed(1) : null} valB={b.psi?.fcp != null ? +(b.psi.fcp / 1000).toFixed(1) : null} unitA=" s" unitB=" s" inverse />
                    <CompareRow label="TBT" valA={a.psi?.tbt} valB={b.psi?.tbt} unitA=" ms" unitB=" ms" inverse />
                  </tbody>
                </table>
              </div>
            )}

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 sm:px-6 py-4">
              <h2 className="text-lg font-bold text-slate-900 mb-3">Schema & security</h2>
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-slate-500 uppercase tracking-wide">
                    <th className="text-left py-2 pr-3 font-medium">Signal</th>
                    <th className="text-center py-2 px-2 font-medium">Your site</th>
                    <th className="text-center py-2 pl-2 font-medium">Competitor</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    ['LocalBusiness schema', a.schemaFlags?.hasLocalBusiness, b.schemaFlags?.hasLocalBusiness],
                    ['Organization schema', a.schemaFlags?.hasOrganization, b.schemaFlags?.hasOrganization],
                    ['Canonical URL', !!a.canonical, !!b.canonical],
                    ['Open Graph title', !!a.ogTitle, !!b.ogTitle],
                    ['Open Graph image', !!a.ogImage, !!b.ogImage],
                    ['HSTS header', !!a.securityHeaders?.strictTransportSecurity, !!b.securityHeaders?.strictTransportSecurity],
                    ['CSP header', !!a.securityHeaders?.contentSecurityPolicy, !!b.securityHeaders?.contentSecurityPolicy],
                    ['robots.txt', a.robotsTxt?.exists, b.robotsTxt?.exists],
                    ['sitemap.xml', a.sitemap?.exists, b.sitemap?.exists],
                  ].map(([label, vA, vB]) => (
                    <tr key={label} className="border-b border-slate-100 last:border-0">
                      <td className="py-2.5 pr-3 text-slate-700 font-medium">{label}</td>
                      <td className="py-2.5 px-2 text-center">
                        {vA ? <FiCheckCircle className="inline text-emerald-600" /> : <FiAlertCircle className="inline text-amber-600" />}
                      </td>
                      <td className="py-2.5 pl-2 text-center">
                        {vB ? <FiCheckCircle className="inline text-emerald-600" /> : <FiAlertCircle className="inline text-amber-600" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <CheckCompare title="Technical SEO checks" checksA={a.technicalChecks} checksB={b.technicalChecks} defaultOpen />
            <CheckCompare title="On-page SEO checks" checksA={a.onPageChecks} checksB={b.onPageChecks} defaultOpen />
            <CheckCompare title="Performance & visibility checks" checksA={a.visibilityChecks} checksB={b.visibilityChecks} defaultOpen={!!(a.psi || b.psi)} />

            <div className="border-t border-slate-200 pt-6 text-center">
              <p className="text-sm text-slate-500 mb-4">
                Want a deeper analysis or help fixing the gaps?
              </p>
              <a href={`${SITE_ORIGIN}/#contact`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 transition-colors shadow-sm">
                Get in touch
              </a>
            </div>
          </section>
        )}
        <Footer />
      </main>
    </div>
  );
}
